// Reading progress for the bar of the Lesen stage. Pure functions, no DOM: list states and streamed observations
// give the milestones, a slow creep keeps the bar moving while the model writes the part that does not stream.
import type { ListKey, ListState, StreamItem } from '../shared/types.js';

const FETCH_KEYS: ListKey[] = ['start', 'ueber-uns', 'leistungen', 'impressum', 'register'];
const BUSY: ListState[] = ['reading', 'matching', 'comparing'];
const FINAL: ListState[] = ['read', 'matched', 'compared', 'missing', 'failed'];
/** Share of the bar per list entry. The observations take 45, the closing stretch up to «done» the rest. */
const WEIGHTS: Record<ListKey, number> = { start: 12, 'ueber-uns': 5, leistungen: 5, impressum: 3, register: 3, visual: 7 };
const OBSERVATION_SHARE = 45;
/** Page observations of a typical run; the first real runs had 24 and 30, the fixture has 18. */
const EXPECTED_OBSERVATIONS = 24;
/** The bar never shows more than this before «done», and never runs further ahead of the milestones than LEAD. */
const CAP = 97;
const LEAD = 17;
const RATE = 0.03;

export type ReadingStep = { kind: 'list'; key: ListKey; state: ListState } | { kind: 'collecting' } | { kind: 'idle' };

export function pageObservations(stream: StreamItem[]): number {
  return stream.filter((item) => item.source === 'page').length;
}

/** Progress in percent that the events alone justify: 0 to 80. */
export function milestone(list: Partial<Record<ListKey, ListState>>, observations: number): number {
  let value = OBSERVATION_SHARE * Math.min(1, observations / EXPECTED_OBSERVATIONS);
  for (const key of Object.keys(WEIGHTS) as ListKey[]) {
    const state = list[key] ?? 'waiting';
    if (FINAL.includes(state)) value += WEIGHTS[key];
    else if (BUSY.includes(state)) value += WEIGHTS[key] * 0.3;
  }
  return value;
}

/** One tick of the bar: never backwards, a little closer to the ceiling above the current milestone. */
export function creep(shown: number, target: number): number {
  const base = Math.max(shown, target);
  const ceiling = Math.min(CAP, target + LEAD);
  return base >= ceiling ? base : base + (ceiling - base) * RATE;
}

/** The bar of a wait that sends no events (call 1b): it follows the time against the usual duration, fast at first,
 * then ever slower, and like the reading bar it never shows more than CAP before the answer is there. */
export function waitProgress(elapsedMs: number, expectedMs: number): number {
  if (elapsedMs <= 0 || expectedMs <= 0) return 0;
  return CAP * (1 - Math.exp(-elapsedMs / expectedMs));
}

/** What the label under the title names: the page in work, then the observations, then the Erscheinungsbild. */
export function readingStep(list: Partial<Record<ListKey, ListState>>, observations: number): ReadingStep {
  for (const key of FETCH_KEYS) {
    const state = list[key] ?? 'waiting';
    if (BUSY.includes(state)) return { kind: 'list', key, state };
  }
  if (observations > 0) return { kind: 'collecting' };
  const visual = list.visual ?? 'waiting';
  if (BUSY.includes(visual)) return { kind: 'list', key: 'visual', state: visual };
  return { kind: 'idle' };
}
